import { useState } from "react";
import { toast } from "sonner";
import { Screen, Spot, Hotspot } from "@/components/site/Screen";
import { NavSpots } from "@/components/site/ArtPage";
import { HouseDialog } from "@/components/site/HouseDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errorText } from "@/lib/api";
import { useIsMobile } from "@/lib/hooks";
import { INSTAGRAM, OWNER_EMAIL } from "@/components/site/siteData";

const SUBJECTS = ["General question", "Service question", "Mobile appointment", "Other"];
const EMPTY = { name: "", email: "", subject: "", message: "" };

function useContact(onDone) {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.subject) { toast.error("Please select a topic."); return; }
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent! Smiless will follow up within 24 hours. 💜");
      setForm(EMPTY);
      onDone?.();
    } catch (err) {
      toast.error(errorText(err, `Message could not be sent. Please email ${OWNER_EMAIL}.`));
    } finally {
      setSending(false);
    }
  };
  return { form, set, submit, sending };
}

const SubjectSelect = ({ value, onChange, className }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className={className} data-testid="contact-subject-select">
      <SelectValue placeholder="Select a topic" />
    </SelectTrigger>
    <SelectContent className="lux-menu" data-lenis-prevent>
      {SUBJECTS.map((s) => <SelectItem key={s} value={s} className="lux-item">{s}</SelectItem>)}
    </SelectContent>
  </Select>
);

const OverlayForm = () => {
  const { form, set, submit, sending } = useContact();
  return (
    <form onSubmit={submit} data-testid="contact-form">
      <Spot box={[53.5, 39.05, 39.6, 2.7]}>
        <input className="ov-control" required placeholder="Name" value={form.name}
          onChange={(e) => set("name")(e.target.value)} data-testid="contact-name-input" />
      </Spot>
      <Spot box={[53.5, 44.6, 39.6, 2.7]}>
        <input className="ov-control" type="email" required placeholder="Email" value={form.email}
          onChange={(e) => set("email")(e.target.value)} data-testid="contact-email-input" />
      </Spot>
      <Spot box={[53.5, 50.4, 39.6, 2.75]}>
        <SubjectSelect value={form.subject} onChange={set("subject")} className="ov-control" />
      </Spot>
      <Spot box={[53.5, 55.95, 39.6, 7.45]}>
        <textarea className="ov-control ov-textarea" required placeholder="Type your message here..."
          value={form.message} onChange={(e) => set("message")(e.target.value)} data-testid="contact-message-input" />
      </Spot>
      <Spot box={[53.9, 64.7, 38.8, 3.3]}>
        <button type="submit" className="ov-submit" disabled={sending} aria-label="Send message"
          data-testid="contact-submit-button">{sending ? <span className="ov-sending">Sending…</span> : null}</button>
      </Spot>
    </form>
  );
};

const StackedForm = ({ onDone }) => {
  const { form, set, submit, sending } = useContact(onDone);
  return (
    <form onSubmit={submit} className="st-form" data-testid="contact-form">
      <label className="st-label">Your Name *<input className="st-control" required value={form.name}
        onChange={(e) => set("name")(e.target.value)} data-testid="contact-name-input" /></label>
      <label className="st-label">Your Email *<input className="st-control" type="email" required value={form.email}
        onChange={(e) => set("email")(e.target.value)} data-testid="contact-email-input" /></label>
      <div className="st-label">Subject *<SubjectSelect value={form.subject} onChange={set("subject")} className="st-control" /></div>
      <label className="st-label">Your Message *<textarea className="st-control st-textarea" required value={form.message}
        onChange={(e) => set("message")(e.target.value)} data-testid="contact-message-input" /></label>
      <button type="submit" className="cta w-full" disabled={sending} data-testid="contact-submit-button">
        {sending ? "Sending…" : "Send Message"}
      </button>
    </form>
  );
};

export const ContactPage = ({ go }) => {
  const mobile = useIsMobile();
  const [open, setOpen] = useState(false);
  return (
    <section aria-label="Contact the House">
      <Screen page="contact">
        <NavSpots page="contact" go={go} />
        <Hotspot x={12} y={35.2} w={33} h={3.9} label="Open Instagram" testId="contact-instagram-link"
          onClick={() => window.open(INSTAGRAM, "_blank", "noopener")} />
        <a className="contact-email" href={`mailto:${OWNER_EMAIL}`} data-testid="contact-email-link">{OWNER_EMAIL}</a>
        {mobile ? (
          <Hotspot x={50} y={30} w={45} h={43} label="Open contact form" onClick={() => setOpen(true)}
            testId="contact-open-form-mobile" />
        ) : <OverlayForm />}
      </Screen>
      <HouseDialog open={open} onOpenChange={setOpen} title="Send Me a Message 💋"
        description="Questions, concerns, or just wanna chat? I got you, boo." testId="contact-dialog">
        <StackedForm onDone={() => setOpen(false)} />
      </HouseDialog>
    </section>
  );
};
